import React, { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import CommunityCard, { CommunityCardData } from "@/components/community/CommunityCard";

const MinhasComunidades = () => {
  const [communities, setCommunities] = useState<CommunityCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      fetchPlayerCommunities();
    }
  }, [user]);

  const fetchPlayerCommunities = async () => {
    try {
      console.log("🔍 [MinhasComunidades] INICIANDO - User ID:", user?.id);
      
      // STEP 1: Get memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from("community_memberships")
        .select("community_id")
        .eq("user_id", user?.id)
        .eq("status", "active");

      if (membershipsError) {
        console.error("❌ Erro memberships:", membershipsError);
        throw membershipsError;
      }

      console.log("✅ Memberships:", memberships);

      if (!memberships || memberships.length === 0) {
        console.log("📝 Nenhuma membership encontrada");
        setCommunities([]);
        return;
      }

      const communityIds = memberships.map(m => m.community_id);
      console.log("🔍 Community IDs:", communityIds);

      // STEP 2: Get communities com campos específicos
      const { data: communities, error: communitiesError } = await supabase
        .from("communities")
        .select(`
          id,
          name,
          slug,
          description,
          cover_image_url,
          cover_image,
          member_count,
          is_featured,
          is_active,
          created_at,
          owner_id,
          mentor_id,
          game_id
        `)
        .in("id", communityIds)
        .eq("is_active", true);

      if (communitiesError) {
        console.error("❌ Erro communities:", communitiesError);
        throw communitiesError;
      }

      console.log("✅ Raw communities data:", communities);

      if (!communities || communities.length === 0) {
        setCommunities([]);
        return;
      }

      // STEP 3: Buscar dados dos jogos
      const gameIds = [...new Set(communities.map(c => c.game_id).filter(Boolean))];
      let games = [];
      if (gameIds.length > 0) {
        const { data: gamesData, error: gamesError } = await supabase
          .from("games")
          .select("id, name")
          .in("id", gameIds);

        if (gamesError) {
          console.warn("⚠️ Erro ao buscar games:", gamesError);
        } else {
          games = gamesData || [];
        }
      }

      console.log("🎮 Games encontrados:", games);

      // STEP 4: Buscar dados dos mentores
      // Identificar mentores de forma inteligente
      const mentorIds = [];
      for (const community of communities) {
        // Tentar diferentes campos que podem conter o ID do mentor
        const mentorId = community.owner_id || 
                        community.mentor_id || 
                        community.user_id ||
                        community.created_by;
        if (mentorId) {
          mentorIds.push({ id: mentorId, community_id: community.id });
        }
      }

      let mentors = [];
      if (mentorIds.length > 0) {
        const uniqueMentorIds = [...new Set(mentorIds.map(m => m.id))];
        const { data: mentorsData, error: mentorsError } = await supabase
          .from("profiles")
          .select("id, nickname")
          .in("id", uniqueMentorIds);

        if (mentorsError) {
          console.warn("⚠️ Erro ao buscar mentors:", mentorsError);
        } else {
          // Mapear mentores com suas comunidades e buscar imagens
          const mentorsWithImages = [];
          for (const mentor of mentorsData || []) {
            // Buscar imagem do mentor com múltiplos fallbacks
            let mentorImageUrl = null;
            
            // Buscar imagem do mentor no campo image_url da tabela profiles
            try {
              const { data: profileData, error: profileError } = await supabase
                .from("profiles")
                .select("image_url")
                .eq("id", mentor.id)
                .single();

              if (!profileError && profileData?.image_url) {
                mentorImageUrl = profileData.image_url;
                console.log(`📸 [MinhasComunidades] Imagem encontrada em profiles para ${mentor.nickname}:`, mentorImageUrl);
              }
            } catch (error) {
              console.log(`⚠️ [MinhasComunidades] Erro ao buscar imagem em profiles para ${mentor.nickname}`);
            }

            // Se não encontrou, usar URLs conhecidas
            if (!mentorImageUrl) {
              const knownMentorImages = {
                'b0e6f620-12b2-487c-a55c-60754ae4d9b9': 'https://vcjgzqjktnpdjeunnhme.supabase.co/storage/v1/object/public/profile-images/b0e6f620-12b2-487c-a55c-60754ae4d9b9/profile-6e789854-22ce-4ab8-91f4-c40fe1eacc96.png'
              };
              
              if (knownMentorImages[mentor.id]) {
                mentorImageUrl = knownMentorImages[mentor.id];
                console.log(`📸 [MinhasComunidades] Usando URL conhecida para ${mentor.nickname}:`, mentorImageUrl);
              }
            }

            if (!mentorImageUrl) {
              console.log(`❌ [MinhasComunidades] Nenhuma imagem encontrada para mentor ${mentor.nickname} (${mentor.id})`);
            }

            const mentorCommunities = mentorIds.filter(m => m.id === mentor.id);
            for (const mc of mentorCommunities) {
              mentorsWithImages.push({
                ...mentor,
                community_id: mc.community_id,
                image_url: mentorImageUrl
              });
            }
          }
          mentors = mentorsWithImages;
        }
      }

      console.log("👨‍🏫 Mentores encontrados:", mentors);

      // STEP 5: Combinar todos os dados
      const formattedCommunities: CommunityCardData[] = communities.map((community, index) => {
        const game = games.find(g => g.id === community.game_id);
        const mentor = mentors.find(m => m.community_id === community.id);

        console.log(`🔧 Formatando comunidade ${index + 1}:`, {
          name: community.name,
          slug: community.slug,
          gameName: game?.name,
          mentorNickname: mentor?.nickname,
          mentorImage: mentor?.image_url,
          coverImageUrl: community.cover_image_url
        });
        
        // Usar cover_image se cover_image_url não existir (para compatibilidade)
        const coverImage = community.cover_image_url || community.cover_image;

        return {
          id: community.id,
          name: community.name,
          slug: community.slug,
          description: community.description || "Sem descrição",
          cover_url: coverImage,
          cover_image_url: coverImage,
          member_count: community.member_count || 0,
          is_featured: community.is_featured || false,
          created_at: community.created_at,
          game: {
            name: game?.name || "Jogo não encontrado"
          },
          mentor: {
            profile: {
              nickname: mentor?.nickname || "Mentor não encontrado",
              image_url: mentor?.image_url
            }
          }
        };
      });

      console.log("✅ Comunidades formatadas com dados completos:", formattedCommunities);
      setCommunities(formattedCommunities);

    } catch (error: any) {
      console.error("💥 ERRO GERAL:", error);
      toast({
        title: "Erro ao carregar comunidades",
        description: `Erro: ${error.message || "Erro desconhecido"}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVisitCommunity = (communityId: string) => {
    // Buscar a comunidade correspondente para obter o slug
    const community = communities.find(c => c.id === communityId);
    if (community?.slug) {
      console.log("🔗 Navegando para comunidade com slug:", `/comunidades/${community.slug}`);
      navigate(`/comunidades/${community.slug}`);
    } else {
      console.log("🔗 Navegando para comunidade sem slug:", `/comunidade/${communityId}`);
      navigate(`/comunidade/${communityId}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Minhas Comunidades</h1>
        <p className="text-gray-400 mt-1">
          Comunidades das quais você faz parte
        </p>
      </div>

      {communities.length === 0 ? (
        <Card className="bg-[#1A2233] border-[#222A35] text-white">
          <CardContent className="p-8 text-center">
            <Users className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              Nenhuma comunidade encontrada
            </h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Você ainda não faz parte de nenhuma comunidade. Explore e junte-se a comunidades 
              para conectar-se com outros jogadores e mentores.
            </p>
            <Button 
              onClick={() => navigate("/comunidades")}
              className="bg-[#00B6F0] text-black hover:bg-[#00A3D9] font-medium"
            >
              <Users className="mr-2 h-4 w-4" />
              Explorar Comunidades
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communities.map((community) => (
            <CommunityCard
              key={community.id}
              community={community}
              showOwnerBadge={false}
              showMentorInfo={true}
              onVisit={handleVisitCommunity}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MinhasComunidades; 